// The WRITE side of the workout<->(session, sets) bridge.
//
// Split out of workoutAssembly.ts (SP-WORKOUTASSEMBLY), which had grown to 475
// lines holding both directions. The two halves never called each other and
// shared no state, so the seam was already there — this only makes it a file
// boundary. `assembleWorkout` (read) stays in workoutAssembly.ts; both are
// re-exported from the package barrel, so no consumer import changed.

import type {
  Dropset,
  LogSetKind,
  NewLogSet,
} from "./schemas/logSet.js";
import type { NewSession } from "./schemas/session.js";
import { TagCapError } from "./error.js";
import { EQUIPMENT_INSTANCE_NS, LOCATION_NS } from "./schemas/tag.js";
import type { NewWorkout } from "./schemas/workout.js";

// Workout → (NewSession, NewLogSet[]) splitter. Mirror of assembleWorkout
// for the write side: a workout-centric payload becomes a session
// envelope plus a flat set stream. Used by the web logger to write
// natively against /sessions + /sets/batch without going through the
// /workouts facade.
//
// Tag namespacing on each set:
//   - `session:<sessionId>`            — ⚠️ NOT stamped here. The API adds
//                                        it (setFactory.stampSystemTags)
//                                        after this splitter runs, so it is
//                                        absent from the count the ceiling
//                                        below checks. The splitter reserves
//                                        that one slot: CLIENT_TAG_CEILING is
//                                        19 (MAX_SET_TAGS − 1), so a payload
//                                        that passes never overflows once the
//                                        server appends `session:`, and no
//                                        tag is truncated (CN-TAGCAP-RESERVE).
//                                        Before the reserve the server cut the
//                                        athlete's last catalog tag; before
//                                        CN-TAGCAP-ASYM it cut `session:`
//                                        itself. The overflow now throws a
//                                        TagCapError the athlete sees inline
//                                        (CN-TAGCAP-THROW). Seam pinned in
//                                        apps/api/__tests__/setTagCapSeam.test.ts.
//   - `entry:<entryIdx>`               — entry-position discriminator
//                                        stamped on EVERY set so the
//                                        assembler can distinguish two
//                                        visits of the same exercise
//                                        in one workout (S6, 0.66.0)
//   - `superset:<entry.group.id>`      — membership tag for both
//                                        superset and circuit kinds;
//                                        the alternation detector
//                                        keys off this prefix
//   - `groupkind:circuit`              — sidecar emitted only when the
//                                        entry's group.kind ===
//                                        "circuit"; default is superset
//                                        so legacy rows round-trip
//                                        without a backfill
//   - `equipment-instance:<slug>`      — WHICH physical machine (EQ-1),
//                                        stamped on EVERY set: the
//                                        history is per exercise+tag and
//                                        is grouped off the LogSet
//                                        stream, so a set without it
//                                        would fall outside its own
//                                        machine
//   - `location:<slug>`                — WHERE it was lifted (EQ-1),
//                                        same every-set rule, same reason
//   - `tag:<slug>` × N                 — entry-level catalog tags,
//                                        stamped on the header set only
//                                        (matches the `note` pattern;
//                                        the readers find that set with
//                                        entryHeaderSet)
//
// `fromPlanId` rides on the dedicated LogSet.fromPlanId column. We
// used to also synthesise a `fromplan:<id>` tag here, but the value
// can be a non-slug composite (e.g. `prog/<uuid>/wk1/sess0/i9`)
// which fails the Tag regex and rejects the whole batch payload.
// The column carries the same information and nothing read the tag.
//
// performedAt fallback: when a set has no completedAt, derive
// `session.startedAt + (entryIdx*1000 + setIdx) ms` so the gsi1 sort
// stays stable. The fallback only matters for never-checked sets in a
// hand-typed workout — the per-set complete button always stamps a
// real completedAt.
const FALLBACK_DELTA_MS = 1;

// Mirrors NewLogSet.tags Zod cap; declared here so the splitter can
// fail-fast before constructing a payload that the schema would reject
// downstream with a less actionable message.
const MAX_SET_TAGS = 20;

// The server appends exactly one system tag the client never sees —
// `session:<id>` (setFactory.stampSystemTags) — AFTER the splitter runs.
// The splitter must budget for it: at MAX_SET_TAGS the append pushed the row
// to 21 and the server truncated back to 20, and the tag it dropped was the
// athlete's LAST catalog tag, silently (CN-TAGCAP-RESERVE / CN-TAGCAP-ASYM).
// Reserving the slot here makes 19 the client's ceiling, so nothing ever
// overflows on the server, and the throw fires one tag earlier — where the
// athlete can act on it (CN-TAGCAP-THROW) — instead of at a silent loss.
const RESERVED_SYSTEM_SLOTS = 1;
const CLIENT_TAG_CEILING = MAX_SET_TAGS - RESERVED_SYSTEM_SLOTS;
export function splitWorkout(
  workout: NewWorkout & { id: string },
): { session: NewSession & { id: string }; sets: NewLogSet[] } {
  const startedMs = Date.parse(workout.startedAt);
  const session: NewSession & { id: string } = {
    id: workout.id,
    startedAt: workout.startedAt,
    ...(workout.endedAt ? { endedAt: workout.endedAt } : {}),
    status: "saved",
    ...(workout.name ? { name: workout.name } : {}),
    ...(workout.instructions ? { instructions: workout.instructions } : {}),
    ...(workout.fromPlanId ? { fromPlanId: workout.fromPlanId } : {}),
    // JL's calorie estimate rides from the logger onto the session it writes
    // (HC-5) — persisted beside the measured `calories`, never overwriting it.
    ...(workout.caloriesEstimate
      ? { caloriesEstimate: workout.caloriesEstimate }
      : {}),
    // Forward the original createdAt when the caller sets it. A correction
    // PATCHes in place and drops it (correctWorkout, #38); absent on a fresh
    // workout → server stamps `now`. See edit-integrity audit.
    ...(workout.createdAt ? { createdAt: workout.createdAt } : {}),
  };
  const sets: NewLogSet[] = [];
  for (let entryIdx = 0; entryIdx < workout.entries.length; entryIdx++) {
    const entry = workout.entries[entryIdx]!;
    const performed = entry.sets.map(
      (s, setIdx) =>
        s.completedAt ??
        new Date(
          startedMs + (entryIdx * 1000 + setIdx) * FALLBACK_DELTA_MS,
        ).toISOString(),
    );
    // The entry's header (catalog tags, entry note) goes on the set the
    // readers take as header: the earliest performed, ties to the first in
    // draft order (entryHeaderSet). Draft order alone put it on a later row
    // when sets were completed out of order (#38).
    let headerIdx = 0;
    performed.forEach((p, i) => {
      if (Date.parse(p) < Date.parse(performed[headerIdx]!)) headerIdx = i;
    });
    for (let setIdx = 0; setIdx < entry.sets.length; setIdx++) {
      const s = entry.sets[setIdx]!;
      const performedAt = performed[setIdx]!;
      const tags: string[] = [];
      // Entry-position tag goes on every set (S6, 0.66.0). It's the
      // signal the assembler uses to keep two visits of the same
      // exercise as separate WorkoutExerciseEntry rows. System tags
      // are pushed before any user-derived tag so a tag-overflow
      // (S10) drops the user's catalog tags first, never the system
      // ones — though the cap check below now throws rather than
      // silently truncating.
      tags.push(`entry:${entryIdx}`);
      if (entry.group) {
        tags.push(`superset:${entry.group.id}`);
        if (entry.group.kind === "circuit") tags.push("groupkind:circuit");
      }
      // EQ-1 identity: WHICH machine, and WHERE. Stamped on EVERY set,
      // like `entry:` above and unlike the catalog snapshot below.
      //
      // The asymmetry is the whole point, so it is worth stating: a
      // catalog tag describes the EXERCISE, so one copy per entry is
      // enough and the assembler reads it off entryHeaderSet. An instance
      // tag describes WHAT WAS LIFTED, and Antenore's rule is that the
      // history is per exercise + tag — a history built by grouping the
      // LogSet stream off the per-exercise GSI. Put the tag on set 0
      // only and every later set of the entry falls OUTSIDE its own
      // machine in that grouping, which is the one thing this feature
      // exists to prevent.
      if (entry.equipmentInstance) {
        tags.push(`${EQUIPMENT_INSTANCE_NS}:${entry.equipmentInstance}`);
      }
      if (entry.location) {
        tags.push(`${LOCATION_NS}:${entry.location}`);
      }
      // Catalog-tag snapshot rides on the header set only, same pattern
      // as `note` below. The other sets in the entry don't repeat the tag
      // list; assembleWorkout finds it with entryHeaderSet.
      if (setIdx === headerIdx && entry.tags) {
        for (const t of entry.tags) tags.push(`tag:${t}`);
      }
      if (tags.length > CLIENT_TAG_CEILING) {
        // Fail-fast on overflow (S10, 0.66.0). Silently slicing used to
        // drop trailing user tags or — if the user added many catalog
        // tags before the system ones got pushed in a future refactor —
        // system metadata.
        //
        // The ceiling is CLIENT_TAG_CEILING (19), not MAX_SET_TAGS (20):
        // the splitter reserves the slot the server fills with `session:`,
        // so a payload that passes here never overflows downstream
        // (CN-TAGCAP-RESERVE).
        //
        // A `TagCapError`, not a plain Error (CN-TAGCAP-THROW): on mobile
        // a bare Error fell through `dispositionOf`'s `!isApiError →
        // 'retry'` default and was queued, then stranded as
        // `permanent_fail` — the athlete saw "queued" then "failed", never
        // which entry. The typed error is recognized as a local verdict,
        // so mobile surfaces it inline exactly as web-rnw already did.
        throw new TagCapError(entryIdx, tags.length, CLIENT_TAG_CEILING);
      }

      // `side` and `duration` only exist on a subset of WorkoutSet
      // variants (cardio omits both for now), so we read through `in`
      // guards before spreading. Same physical fields as before — the
      // guards just keep TypeScript happy after the union widened.
      const sideField =
        "side" in s && s.side !== undefined ? { side: s.side } : {};
      const durationField =
        "duration" in s && s.duration !== undefined
          ? { duration: s.duration }
          : {};
      const base: Omit<NewLogSet, "kind"> & { kind: LogSetKind } = {
        exerciseSlug: entry.exerciseId,
        kind: s.type as LogSetKind,
        performedAt,
        ...(s.completedAt ? { completedAt: s.completedAt } : {}),
        ...(s.rpe !== undefined ? { rpe: s.rpe } : {}),
        ...(s.rir !== undefined ? { rir: s.rir } : {}),
        ...durationField,
        ...sideField,
        ...(s.fail !== undefined ? { fail: s.fail } : {}),
        sessionId: workout.id,
        ...(workout.fromPlanId ? { fromPlanId: workout.fromPlanId } : {}),
        ...(tags.length > 0 ? { tags } : {}),
      };
      // Note storage. The header set's `note` column doubles as the entry-
      // level header (legacy behaviour preserved): if entry.notes is
      // present it wins on the header set. Otherwise the per-set note (if
      // any) is written. Every other set carries its own note
      // independently and assembleWorkout reads it back as
      // entry.sets[i].note. Single-column trade-off documented; full
      // disambiguation can ride a later schema migration.
      if (setIdx === headerIdx && entry.notes) {
        (base as { note?: string }).note = entry.notes;
      } else if (
        typeof (s as { note?: string }).note === "string" &&
        (s as { note: string }).note.length > 0
      ) {
        (base as { note?: string }).note = (s as { note: string }).note;
      }

      // Per-kind payload + intra-set dropset chain.
      let payload: NewLogSet;
      if (s.type === "weighted") {
        payload = {
          ...base,
          reps: s.reps,
          weight: s.weight,
          ...(s.loadedSides !== undefined
            ? { loadedSides: s.loadedSides }
            : {}),
          ...(s.weightSource !== undefined
            ? { weightSource: s.weightSource }
            : {}),
          // Omit-the-default: only `true` reaches DDB, so an untouched
          // set stays indistinguishable from a legacy row.
          ...(s.amrap === true ? { amrap: true } : {}),
          ...(s.dropset ? { dropset: s.dropset as Dropset } : {}),
        };
      } else if (s.type === "bodyweight") {
        payload = {
          ...base,
          reps: s.reps,
          ...(s.extra !== undefined ? { extra: s.extra } : {}),
          ...(s.weightSource !== undefined
            ? { weightSource: s.weightSource }
            : {}),
          ...(s.amrap === true ? { amrap: true } : {}),
          ...(s.dropset ? { dropset: s.dropset as Dropset } : {}),
        };
      } else if (s.type === "p1rm") {
        payload = {
          ...base,
          reps: s.reps,
          p1rm: s.p1rm,
          rfl: s.rfl,
        };
      } else if (s.type === "cardio") {
        payload = {
          ...base,
          duration: s.duration,
          ...(s.distanceM !== undefined ? { distanceM: s.distanceM } : {}),
          ...(s.weight !== undefined ? { weight: s.weight } : {}),
          ...(s.avgHrBpm !== undefined ? { avgHrBpm: s.avgHrBpm } : {}),
          ...(s.calories !== undefined ? { calories: s.calories } : {}),
        };
      } else if (s.type === "timed") {
        // Isometric hold: duration is already carried by base.durationField;
        // weight (optional added load) rides when present.
        payload = {
          ...base,
          duration: s.duration,
          ...(s.weight !== undefined ? { weight: s.weight } : {}),
        };
      } else {
        payload = {
          ...base,
          weight: s.weight,
          distanceM: s.distanceM,
          ...(s.reps !== undefined ? { reps: s.reps } : {}),
        };
      }
      sets.push(payload);
    }
  }
  return { session, sets };
}
